import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';

dayjs.extend(isBetween);

const PRESET_RANGES = [
  { label: 'Last 7 days', getValue: () => [dayjs().subtract(7, 'day'), dayjs()] },
  { label: 'Last 30 days', getValue: () => [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'Last 3 months', getValue: () => [dayjs().subtract(3, 'month'), dayjs()] },
  { label: 'Last 6 months', getValue: () => [dayjs().subtract(6, 'month'), dayjs()] },
  { label: 'This year', getValue: () => [dayjs().startOf('year'), dayjs()] },
];

function Calendar({ startDate, endDate, onSelect, currentMonth, onMonthChange, hoverDate }) {
  const daysInMonth = currentMonth.daysInMonth();
  const firstDayOfMonth = currentMonth.startOf('month').day();
  const days = [];

  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i += 1) {
    days.push(<div key={`empty-${i}`} className="h-8 w-8" />);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = currentMonth.date(day);
    const isStartDate = startDate && date.isSame(startDate, 'day');
    const isEndDate = endDate && date.isSame(endDate, 'day');
    const isToday = date.isSame(dayjs(), 'day');

    // Check if date is in range
    let isInRange = false;
    if (startDate && endDate) {
      isInRange = date.isBetween(startDate, endDate, 'day', '()');
    } else if (startDate && hoverDate && !endDate) {
      const rangeStart = startDate.isBefore(hoverDate) ? startDate : hoverDate;
      const rangeEnd = startDate.isBefore(hoverDate) ? hoverDate : startDate;
      isInRange = date.isBetween(rangeStart, rangeEnd, 'day', '()');
    }

    days.push(
      <button
        key={day}
        type="button"
        onClick={() => onSelect(date)}
        className={cn(
          'h-8 w-8 text-sm transition-colors relative',
          'hover:bg-accent hover:text-accent-foreground rounded-md',
          isInRange && 'bg-primary/10 rounded-none',
          (isStartDate || isEndDate) &&
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-md z-10',
          isStartDate && endDate && 'rounded-r-none',
          isEndDate && startDate && 'rounded-l-none',
          isToday && !isStartDate && !isEndDate && 'border border-primary rounded-md',
        )}
      >
        {day}
      </button>,
    );
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
          className="p-1 rounded hover:bg-accent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium">{currentMonth.format('MMMM YYYY')}</span>
        <button
          type="button"
          onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
          className="p-1 rounded hover:bg-accent"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div
            key={d}
            className="h-8 w-8 flex items-center justify-center text-xs text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0">{days}</div>
    </div>
  );
}

Calendar.propTypes = {
  startDate: PropTypes.instanceOf(dayjs),
  endDate: PropTypes.instanceOf(dayjs),
  hoverDate: PropTypes.instanceOf(dayjs),
  onSelect: PropTypes.func.isRequired,
  currentMonth: PropTypes.instanceOf(dayjs).isRequired,
  onMonthChange: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
  startDate: null,
  endDate: null,
  hoverDate: null,
};

function DateRangePicker({ startDate, endDate, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [leftMonth, setLeftMonth] = useState(dayjs().subtract(1, 'month'));
  const [rightMonth, setRightMonth] = useState(dayjs());
  const [hoverDate, setHoverDate] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset temp dates when opening
  useEffect(() => {
    if (isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setLeftMonth(startDate ? startDate.startOf('month') : dayjs().subtract(1, 'month'));
      setRightMonth(endDate ? endDate.startOf('month') : dayjs());
    }
  }, [isOpen, startDate, endDate]);

  const handleDateSelect = (date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start new selection
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (date.isBefore(tempStartDate)) {
      // Complete selection - swap if date is before start
      setTempEndDate(tempStartDate);
      setTempStartDate(date);
    } else {
      // Complete selection - date is after start
      setTempEndDate(date);
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onChange(tempStartDate, tempEndDate);
      setIsOpen(false);
    }
  };

  const handlePresetClick = (preset) => {
    const [start, end] = preset.getValue();
    setTempStartDate(start);
    setTempEndDate(end);
    onChange(start, end);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select date range';
    return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
  };

  const handleLeftMonthChange = (month) => {
    setLeftMonth(month);
    // Ensure right month is always after left month
    if (month.isSame(rightMonth, 'month') || month.isAfter(rightMonth)) {
      setRightMonth(month.add(1, 'month'));
    }
  };

  const handleRightMonthChange = (month) => {
    setRightMonth(month);
    // Ensure left month is always before right month
    if (month.isSame(leftMonth, 'month') || month.isBefore(leftMonth)) {
      setLeftMonth(month.subtract(1, 'month'));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
          'bg-background border border-input',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
        )}
      >
        <CalendarIcon className="h-4 w-4" />
        <span>{formatDateRange()}</span>
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 z-50',
            'bg-popover border border-border rounded-lg shadow-lg',
          )}
        >
          <div className="flex">
            {/* Presets */}
            <div className="border-r border-border p-3 w-40">
              <p className="text-xs font-medium text-muted-foreground mb-2">Quick Select</p>
              <div className="space-y-1">
                {PRESET_RANGES.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 text-sm rounded',
                      'hover:bg-accent hover:text-accent-foreground',
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendars */}
            <div className="flex" onMouseLeave={() => setHoverDate(null)}>
              <div
                className="border-r border-border"
                onMouseMove={(e) => {
                  if (!tempEndDate && tempStartDate) {
                    const { target } = e;
                    if (target.tagName === 'BUTTON' && target.textContent) {
                      const day = parseInt(target.textContent, 10);
                      if (!Number.isNaN(day)) {
                        setHoverDate(leftMonth.date(day));
                      }
                    }
                  }
                }}
              >
                <Calendar
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  hoverDate={hoverDate}
                  onSelect={handleDateSelect}
                  currentMonth={leftMonth}
                  onMonthChange={handleLeftMonthChange}
                />
              </div>
              <div
                onMouseMove={(e) => {
                  if (!tempEndDate && tempStartDate) {
                    const { target } = e;
                    if (target.tagName === 'BUTTON' && target.textContent) {
                      const day = parseInt(target.textContent, 10);
                      if (!Number.isNaN(day)) {
                        setHoverDate(rightMonth.date(day));
                      }
                    }
                  }
                }}
              >
                <Calendar
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  hoverDate={hoverDate}
                  onSelect={handleDateSelect}
                  currentMonth={rightMonth}
                  onMonthChange={handleRightMonthChange}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-border flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {tempStartDate && !tempEndDate && 'Select end date'}
              {tempStartDate && tempEndDate && (
                <span>
                  {tempStartDate.format('MMM D, YYYY')} - {tempEndDate.format('MMM D, YYYY')}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleApply} disabled={!tempStartDate || !tempEndDate}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DateRangePicker.propTypes = {
  startDate: PropTypes.instanceOf(dayjs).isRequired,
  endDate: PropTypes.instanceOf(dayjs).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DateRangePicker;
